import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import ConnectLessenLayout from '@/components/ConnectLessenLayout';
import LessonShareDialog from '@/features/connectlessen/share/LessonShareDialog';
import RecipientsPanel from '@/features/connectlessen/recipients/RecipientsPanel';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { BookOpen, Pencil, Plus, Send, Share2, Trash2, Undo2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Onder 768 px: touch-doelen van minimaal 44 px en labels die mogen afbreken.
const MOBILE_ACTION = 'max-md:h-auto max-md:min-h-11 max-md:w-full max-md:whitespace-normal';

const EMPTY_LESSON_FORM = {
  title: '',
  series: '',
  week_number: '',
  lesson_date: '',
  opening_welcome: '',
  opening_prayer: '',
  opening_introductions: '',
  opening_testimonies: '',
  theme: '',
  introduction: '',
  bible_passages: [],
  study_synthesis: '',
  key_points: '',
  questions: [],
  practical_application: '',
  prayer_points: '',
  agenda_note: '',
  closing: '',
};

const ConnectLessenPage = () => {
  const [loading, setLoading] = useState(true);
  const [lessons, setLessons] = useState([]);
  const [statusFilter, setStatusFilter] = useState('alle');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [form, setForm] = useState(EMPTY_LESSON_FORM);
  const { hasCapability } = useAuth();
  const canShare = hasCapability('connectlessen_share');
  const [shareLesson, setShareLesson] = useState(null);
  const canManageRecipients = hasCapability('connectlessen_recipients_manage');
  const [view, setView] = useState('lessen');
  const [busyLessonIds, setBusyLessonIds] = useState(() => new Set());
  const busyLessonIdsRef = useRef(new Set());

  useEffect(() => {
    fetchLessons();
  }, []);

  const fetchLessons = async () => {
    try {
      const response = await axios.get(`${API}/connectlessen`);
      setLessons(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kon connectlessen niet laden');
    } finally {
      setLoading(false);
    }
  };

  const filteredLessons = useMemo(() => {
    if (statusFilter === 'alle') return lessons;
    return lessons.filter((l) => l.status === statusFilter);
  }, [lessons, statusFilter]);

  const resetForm = () => {
    setEditingLesson(null);
    setForm(EMPTY_LESSON_FORM);
  };

  const openEdit = (lesson) => {
    setEditingLesson(lesson);
    setForm({
      title: lesson.title,
      series: lesson.series || '',
      week_number: lesson.week_number || '',
      lesson_date: lesson.lesson_date || '',
      opening_welcome: lesson.opening_welcome || '',
      opening_prayer: lesson.opening_prayer || '',
      opening_introductions: lesson.opening_introductions || '',
      opening_testimonies: lesson.opening_testimonies || '',
      theme: lesson.theme || '',
      introduction: lesson.introduction || '',
      bible_passages: (lesson.bible_passages || []).map((p) => ({ reference: p.reference, explanation: p.explanation || '' })),
      study_synthesis: lesson.study_synthesis || '',
      key_points: lesson.key_points || '',
      questions: (lesson.questions || []).map((q) => ({ text: q.text })),
      practical_application: lesson.practical_application || '',
      prayer_points: lesson.prayer_points || '',
      agenda_note: lesson.agenda_note || '',
      closing: lesson.closing || '',
    });
    setDialogOpen(true);
  };

  const addPassage = () => setForm((f) => ({ ...f, bible_passages: [...f.bible_passages, { reference: '', explanation: '' }] }));
  const updatePassage = (index, field, value) => setForm((f) => ({
    ...f,
    bible_passages: f.bible_passages.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
  }));
  const removePassage = (index) => setForm((f) => ({ ...f, bible_passages: f.bible_passages.filter((_, i) => i !== index) }));

  const addQuestion = () => setForm((f) => ({ ...f, questions: [...f.questions, { text: '' }] }));
  const updateQuestion = (index, value) => setForm((f) => ({
    ...f,
    questions: f.questions.map((q, i) => (i === index ? { text: value } : q)),
  }));
  const removeQuestion = (index) => setForm((f) => ({ ...f, questions: f.questions.filter((_, i) => i !== index) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) {
      toast.error('Titel is verplicht');
      return;
    }
    const payload = {
      ...form,
      week_number: form.week_number ? Number(form.week_number) : null,
      lesson_date: form.lesson_date || null,
      bible_passages: form.bible_passages.filter((p) => p.reference),
      questions: form.questions.filter((q) => q.text),
    };
    try {
      if (editingLesson) {
        await axios.put(`${API}/connectlessen/${editingLesson.id}`, payload);
        toast.success('Connectles bijgewerkt');
      } else {
        await axios.post(`${API}/connectlessen`, payload);
        toast.success('Connectles aangemaakt als concept');
      }
      setDialogOpen(false);
      resetForm();
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Actie mislukt');
    }
  };

  // Eén statusactie tegelijk per les; de knop wisselt na publiceren op dezelfde plek van
  // betekenis, dus een tweede klik mag nooit een tegenovergestelde actie starten.
  const runStatusAction = async (lessonId, action, successMessage, failureMessage) => {
    if (busyLessonIdsRef.current.has(lessonId)) return;
    busyLessonIdsRef.current.add(lessonId);
    setBusyLessonIds((current) => new Set(current).add(lessonId));
    try {
      const response = await axios.post(`${API}/connectlessen/${lessonId}/${action}`);
      setLessons((current) => current.map((lesson) => (lesson.id === lessonId ? response.data : lesson)));
      toast.success(successMessage);
    } catch (error) {
      toast.error(error.response?.data?.detail || failureMessage);
    } finally {
      busyLessonIdsRef.current.delete(lessonId);
      setBusyLessonIds((current) => {
        const next = new Set(current);
        next.delete(lessonId);
        return next;
      });
    }
  };

  const handlePublish = (lessonId) => runStatusAction(lessonId, 'publish', 'Connectles gepubliceerd', 'Publiceren mislukt');

  const handleUnpublish = (lessonId) => {
    if (!window.confirm('Deze connectles terugzetten naar concept? Connectleiders kunnen hem daarna niet meer lezen.')) return;
    runStatusAction(lessonId, 'unpublish', 'Connectles teruggezet naar concept', 'Terugzetten naar concept mislukt');
  };

  const handleDelete = async (lessonId) => {
    if (!window.confirm('Weet je zeker dat je deze connectles wilt verwijderen?')) return;
    try {
      await axios.delete(`${API}/connectlessen/${lessonId}`);
      toast.success('Connectles verwijderd');
      fetchLessons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  if (loading) {
    return (
      <ConnectLessenLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-2xl"></div>
        </div>
      </ConnectLessenLayout>
    );
  }

  return (
    <ConnectLessenLayout>
      <div className="space-y-6" data-testid="connectlessen-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Connectlessen</h1>
            <p className="text-slate-500">Maak en publiceer de wekelijkse connectles</p>
          </div>
          <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl" data-testid="add-lesson-btn">
            <Plus className="w-4 h-4 mr-2" /> Nieuwe connectles
          </Button>
        </div>

        {canManageRecipients && (
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="lessen">Lessen</TabsTrigger>
              <TabsTrigger value="ontvangers">Ontvangers</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {view === 'ontvangers' && canManageRecipients ? <RecipientsPanel /> : (
        <>
        <Tabs value={statusFilter} onValueChange={setStatusFilter}>
          <TabsList>
            <TabsTrigger value="alle">Alle</TabsTrigger>
            <TabsTrigger value="concept">Concepten</TabsTrigger>
            <TabsTrigger value="gepubliceerd">Gepubliceerd</TabsTrigger>
          </TabsList>
          <TabsContent value={statusFilter} className="mt-4">
            {filteredLessons.length === 0 ? (
              <div className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Geen connectlessen</h3>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLessons.map((lesson) => (
                  <Card key={lesson.id} className="border-0 shadow-sm">
                    <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:justify-between" data-testid="lesson-card">
                      <div className="min-w-0 md:flex-1">
                        <div className="md:flex md:items-center md:gap-2">
                          <p className="break-words font-medium text-slate-900 md:truncate">{lesson.title}</p>
                          <div className="mt-2 md:mt-0">
                            <Badge variant={lesson.status === 'gepubliceerd' ? 'default' : 'secondary'}>
                              {lesson.status === 'gepubliceerd' ? 'Gepubliceerd' : 'Concept'}
                            </Badge>
                          </div>
                        </div>
                        <p className="mt-1 break-words text-sm text-slate-500 md:mt-0 md:truncate">
                          {lesson.series ? `${lesson.series} · ` : ''}{lesson.theme || 'Geen thema'}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-2 md:flex md:shrink-0 md:items-center" data-testid="lesson-actions">
                        {canShare && lesson.status === 'gepubliceerd' && (
                          <Button variant="outline" size="sm" className={MOBILE_ACTION} onClick={() => setShareLesson(lesson)} aria-label={`Deel ${lesson.title}`}><Share2 className="w-4 h-4 mr-1" />Delen</Button>
                        )}
                        <Button variant="outline" size="sm" className={MOBILE_ACTION} onClick={() => openEdit(lesson)}><Pencil className="w-4 h-4 max-md:mr-1" /><span className="md:sr-only">Bewerken</span></Button>
                        {lesson.status === 'gepubliceerd' ? (
                          <Button variant="outline" size="sm" className={MOBILE_ACTION} disabled={busyLessonIds.has(lesson.id)} onClick={() => handleUnpublish(lesson.id)}><Undo2 className="w-4 h-4 mr-1" />Depubliceren</Button>
                        ) : (
                          <Button size="sm" disabled={busyLessonIds.has(lesson.id)} onClick={() => handlePublish(lesson.id)} className={`bg-fuchsia-600 hover:bg-fuchsia-700 text-white ${MOBILE_ACTION}`}><Send className="w-4 h-4 mr-1" />Publiceren</Button>
                        )}
                        <Button variant="ghost" size="icon" className="max-md:h-auto max-md:min-h-11 max-md:w-full max-md:border max-md:border-slate-200" onClick={() => handleDelete(lesson.id)}><Trash2 className="w-4 h-4 text-red-500 max-md:mr-1" /><span className="text-red-600 md:sr-only">Verwijderen</span></Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="dialog-sheet-mobile sm:max-w-3xl sm:max-h-[90vh] sm:overflow-y-auto">
            <DialogHeader className="pr-12">
              <DialogTitle className="max-sm:text-left max-sm:leading-snug">{editingLesson ? 'Connectles bewerken' : 'Nieuwe connectles'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titel *</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Serie</Label>
                  <Input value={form.series} onChange={(e) => setForm({ ...form, series: e.target.value })} className="rounded-xl" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Weeknummer</Label>
                  <Input type="number" value={form.week_number} onChange={(e) => setForm({ ...form, week_number: e.target.value })} className="rounded-xl" />
                </div>
                <div className="space-y-2">
                  <Label>Datum</Label>
                  <Input type="date" value={form.lesson_date} onChange={(e) => setForm({ ...form, lesson_date: e.target.value })} className="rounded-xl" />
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                <p className="font-medium text-slate-900">Opening</p>
                <Textarea placeholder="Welkom" value={form.opening_welcome} onChange={(e) => setForm({ ...form, opening_welcome: e.target.value })} className="rounded-xl resize-none" rows={2} />
                <Textarea placeholder="Openingsgebed" value={form.opening_prayer} onChange={(e) => setForm({ ...form, opening_prayer: e.target.value })} className="rounded-xl resize-none" rows={2} />
                <Textarea placeholder="Voorstellen" value={form.opening_introductions} onChange={(e) => setForm({ ...form, opening_introductions: e.target.value })} className="rounded-xl resize-none" rows={2} />
                <Textarea placeholder="Getuigenissen/dankzegging" value={form.opening_testimonies} onChange={(e) => setForm({ ...form, opening_testimonies: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>

              <div className="space-y-2">
                <Label>Thema</Label>
                <Input value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Inleiding</Label>
                <Textarea value={form.introduction} onChange={(e) => setForm({ ...form, introduction: e.target.value })} className="rounded-xl resize-none" rows={3} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Bijbelgedeelten</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addPassage}><Plus className="w-4 h-4 mr-1" />Toevoegen</Button>
                </div>
                {form.bible_passages.map((passage, index) => (
                  <div key={index} className="flex gap-2 items-start rounded-xl border border-slate-200 p-3">
                    <div className="flex-1 space-y-2">
                      <Input placeholder="bv. Filippenzen 3:7-8" value={passage.reference} onChange={(e) => updatePassage(index, 'reference', e.target.value)} className="rounded-xl" />
                      <Textarea placeholder="Toelichting" value={passage.explanation} onChange={(e) => updatePassage(index, 'explanation', e.target.value)} className="rounded-xl resize-none" rows={2} />
                    </div>
                    <Button type="button" variant="ghost" size="icon" onClick={() => removePassage(index)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Afsluitende synthese</Label>
                <Textarea value={form.study_synthesis} onChange={(e) => setForm({ ...form, study_synthesis: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Kernpunten</Label>
                <Textarea value={form.key_points} onChange={(e) => setForm({ ...form, key_points: e.target.value })} className="rounded-xl resize-none" rows={3} />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Gespreksvragen</Label>
                  <Button type="button" variant="outline" size="sm" onClick={addQuestion}><Plus className="w-4 h-4 mr-1" />Toevoegen</Button>
                </div>
                {form.questions.map((question, index) => (
                  <div key={index} className="flex gap-2 items-center">
                    <span className="text-sm text-slate-500 w-6">{index + 1}.</span>
                    <Input value={question.text} onChange={(e) => updateQuestion(index, e.target.value)} className="rounded-xl flex-1" />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeQuestion(index)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Praktische toepassing</Label>
                <Textarea value={form.practical_application} onChange={(e) => setForm({ ...form, practical_application: e.target.value })} className="rounded-xl resize-none" rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Gebedspunten</Label>
                <Textarea value={form.prayer_points} onChange={(e) => setForm({ ...form, prayer_points: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>
              <div className="space-y-2">
                <Label>Gemeenteagenda</Label>
                <Input placeholder="bv. Bidstond dinsdag 20:00" value={form.agenda_note} onChange={(e) => setForm({ ...form, agenda_note: e.target.value })} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>Afsluiting</Label>
                <Textarea value={form.closing} onChange={(e) => setForm({ ...form, closing: e.target.value })} className="rounded-xl resize-none" rows={2} />
              </div>

              <DialogFooter className="dialog-sheet-footer gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">Annuleren</Button>
                <Button type="submit" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white rounded-xl">{editingLesson ? 'Opslaan' : 'Toevoegen als concept'}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </>
        )}
        <LessonShareDialog lesson={shareLesson} open={Boolean(shareLesson)} onOpenChange={(open) => { if (!open) setShareLesson(null); }} />
      </div>
    </ConnectLessenLayout>
  );
};

export default ConnectLessenPage;
